import * as React from 'react';

// @ts-ignore
// eslint-disable-next-line import/no-extraneous-dependencies,ordered-imports/ordered-imports
import { Howl } from 'howler';

import { Cell, Container, Grid } from '@zeiss/ods-components-react';

import { Text, Tile } from '../../components';
import { IScore } from '../../types';
import { useData, useSleepMode } from '../../hooks';

import {
  StyledContainer,
  StyledHeadline,
  StyledOverviewCards,
  StyledOverviewCardsContent,
  StyledScoreTile,
} from './styled';

type Module = {
  [key: string]: {
    backgroundImage?: string;
    id: string;
    quantity: number;
  };
};

const getModules = (scores: Array<IScore>) =>
  scores.reduce((acc, curr: IScore) => {
    const module = curr['module-title'];
    return !acc[module]
      ? {
          ...acc,
          [module]: {
            id: curr.id,
            quantity: 1,
            backgroundImage: `./images/musical-scores-cards/${curr['module-image']}`,
          },
        }
      : acc;
  }, {} as Module);

const OverviewCardsView = () => {
  const { setStartTime } = useSleepMode();
  const { scores = [], details = [] } = useData();

  React.useEffect(() => setStartTime(Date.now()), []);

  const modules = getModules(scores);

  const [sound, setSound] = React.useState<any>();
  React.useEffect(() => {
    const soundObject = new Howl({
      src: ['./audios/interaction-sounds/overview-2-background-loop.mp3'],
      volume: 0.05,
      onload: () => {
        setSound(soundObject);
      },
    });
  }, []);

  React.useEffect(() => {
    if (sound) {
      sound.play();
      sound.fade(0, 0.05, 1000);
    }

    return () => {
      if (sound) {
        sound.fade(0.05, 0, 1000);
      }
    };
  }, [sound]);

  const infoSound = new Howl({
    src: ['./audios/interaction-sounds/overview-2-tap-info.mp3'],
    volume: 0.01,
  });

  const scoreSound = new Howl({
    src: ['./audios/interaction-sounds/overview-2-tap-item.mp3'],
    volume: 0.01,
  });

  return (
    <StyledOverviewCards>
      <Grid>
        <Container.FullBleed>
          <Cell.Basic colStarts={[1, 1, 2]} colSpan={[4, 6, 5]}>
            <StyledContainer>
              <Grid>
                <Container.FullBleed>
                  <Cell.MaxWidth>
                    <StyledOverviewCardsContent>
                      <StyledHeadline
                        size="l"
                        headline="How is novelty created in science, business, and art?"
                      />
                      <Text size="xl">
                        In his creative residency at the ZEISS Innovation Hub @
                        KIT in the fall of 2021, the musician and artist
                        Emmanuel Witzthum engaged with precisely these
                        questions. He accompanied ZEISS employees during the
                        innovation projects and introduced artistic approaches
                        into scientific practise and brought these experiences
                        to bear on his composition „The Contour of Stars“, a
                        graphic score that visualizes the processes that
                        characterized the collaboration. The work was premiered
                        by the Ensemble Modern at the ZKM, Karlsruhe on the
                        2.11.2022.
                      </Text>
                    </StyledOverviewCardsContent>
                  </Cell.MaxWidth>
                  <Cell.MaxWidth>
                    <StyledHeadline
                      size="m"
                      headline="About the Project and the Music"
                    />
                  </Cell.MaxWidth>
                  {details.map((detail) => (
                    <Cell.Basic colSpan={[2, 3, 6]} key={detail.title}>
                      <Tile
                        onClick={() => infoSound.play()}
                        href={`/about/${detail.id}`}
                        backgroundImage={`./images/info-pages-cards/${detail.image}`}
                      >
                        <StyledHeadline
                          size="s"
                          headline={detail.title}
                          subHeadline={detail.subtitle}
                        />
                      </Tile>
                    </Cell.Basic>
                  ))}
                </Container.FullBleed>
              </Grid>
            </StyledContainer>
          </Cell.Basic>
          <Cell.Basic colSpan={[4, 6, 5]}>
            <Grid>
              <Container.FullBleed>
                <Cell.MaxWidth>
                  <StyledHeadline headline="The Musical Scores" size="m" />
                </Cell.MaxWidth>
                {Object.entries(modules).map(([key, value]) => (
                  <Cell.Basic colSpan={[2, 3, 6]} key={key}>
                    <StyledScoreTile
                      onClick={() => scoreSound.play()}
                      href={`/score/${value.id}`}
                      backgroundImage={value.backgroundImage}
                      dark
                    >
                      <StyledHeadline size="s" headline={key} />
                    </StyledScoreTile>
                  </Cell.Basic>
                ))}
              </Container.FullBleed>
            </Grid>
          </Cell.Basic>
        </Container.FullBleed>
      </Grid>
    </StyledOverviewCards>
  );
};

export default OverviewCardsView;
